import { useThemeColors } from "@/hooks/useThemeColors"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import type { SystemStyleObject } from "@chakra-ui/react"
import type { ControllerFieldState } from "react-hook-form"
import type { CSSObjectWithLabel, StylesConfig } from "react-select"

const sc: StylesConfig<{ label: string; value: string }> = {
  option: (base, extra) => ({
    ...base,
  }),
}

export type CustomSettings = {
  variant?: "headFilter" | "whiteBG"
  errorColor?: string
  borderColor?: string
  width?: string
  fontSize?: string
  minHeight?: string | number
}
export const getReactSelectStyles = (
  fieldState: ControllerFieldState | undefined,
  customs: CustomSettings | undefined,
  getThemeColor: (color: string, d?: string) => string | number | undefined
) => {
  const settings = customs ?? ({} as CustomSettings)

  // BARNES Design System select (dist/css/select.css), semantic tokens only.
  const text = getThemeColor("text", "#000000")
  const textMuted = getThemeColor("textMuted", "#4A4A4A")
  const textSubtle = getThemeColor("textSubtle", "#767676")
  const lineStrong = getThemeColor("lineStrong", "#B5AFA3")
  const line = getThemeColor("line", "#D8D4CB")
  const surface = getThemeColor("surface", "#FFFFFF")
  const surfaceAlt = getThemeColor("surfaceAlt", "#F7F5F1")
  const surfaceGreige = getThemeColor("surfaceGreige", "#EAE6DD")
  const danger = getThemeColor("danger", "#C70A33")
  // Callers pass a theme color name ("line") or an already resolved value.
  const borderColor = settings.borderColor
    ? getThemeColor(settings.borderColor, settings.borderColor)
    : lineStrong

  if (settings.variant === "headFilter") {
    // Toolbar filters: compact 40px, like the search field next to them (ADR 0001, Q15).
    settings.minHeight = 40
  } else if (settings.variant === "whiteBG") {
    settings.width = "stretch"
  }

  return {
    container: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        width: settings?.width ?? base.width,
        fontSize: settings?.fontSize ?? base.fontSize,
      }) as T,
    control: <T extends CSSObjectWithLabel | SystemStyleObject>(
      base: T,
      state: { isFocused: boolean }
    ) =>
      ({
        ...base,
        width: "stretch",
        borderRadius: 4,
        backgroundColor: surface,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: state.isFocused
          ? text
          : fieldState?.invalid
            ? (settings?.errorColor ?? danger)
            : borderColor,
        boxShadow: "none",
        outline: "none",
        minHeight: settings?.minHeight ?? 48,
        ":hover": {
          borderColor: state.isFocused
            ? text
            : fieldState?.invalid
              ? (settings?.errorColor ?? danger)
              : lineStrong,
        },
      }) as T,
    indicatorSeparator: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        display: "none",
      }) as T,
    placeholder: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        color: textSubtle,
      }) as T,
    singleValue: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        color: text,
      }) as T,
    multiValue: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        backgroundColor: surface,
        border: `1px solid ${lineStrong}`,
        borderRadius: 4,
        color: text,
      }) as T,
    multiValueLabel: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        color: text,
      }) as T,
    multiValueRemove: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        color: textMuted,
        ":hover": {
          backgroundColor: surfaceGreige,
          color: text,
        },
      }) as T,
    dropdownIndicator: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) => ({
      ...base,
      padding: 5,
      color: textMuted,
    }),
    menu: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        borderRadius: 4,
        border: `1px solid ${line}`,
        boxShadow: "0 8px 40px rgba(0, 0, 0, 0.16)",
      }) as T,
    option: <T extends CSSObjectWithLabel | SystemStyleObject>(
      base: T,
      state: {
        isSelected: boolean
        isFocused: boolean
      } /*data: Option, isDisabled: boolean, isMulti: boolean, label: string...*/
    ) => ({
      ...base,
      color: text,
      fontWeight: state.isSelected ? 600 : 400,
      backgroundColor: state.isFocused ? surfaceAlt : "transparent",
      ":active": {
        backgroundColor: surfaceAlt,
      },
      ":hover": {
        backgroundColor: surfaceAlt,
      },
    }),
    valueContainer: <T extends CSSObjectWithLabel | SystemStyleObject>(base: T) =>
      ({
        ...base,
        padding: "1px 16px",
      }) as T,
  }
}

export const useGetReactSelectStyles = (
  fieldState?: ControllerFieldState,
  settings?: CustomSettings
) => {
  const { getThemeColor } = useThemeColors()

  return getReactSelectStyles(
    fieldState,
    settings,
    isNotNullNorUndefined(getThemeColor)
      ? getThemeColor
      : (c: string, d?: string | number) => c.split(".").shift() ?? d ?? ""
  )
}
